import {
  Box,
  Divider,
  IconButton,
  makeStyles,
  SvgIcon,
  useTheme,
} from '@material-ui/core';
import ConverTagsListView from '@src/components/AdminHome/Tags/ConverTagsListView';
import AttachFilesSummary from '@src/components/AttachFilesSummary';
import ChatQuotedMessagePreview from '@src/components/ChatMessageComposer/ChatQuotedMessagePreview';
import { MAX_ROWS } from '@src/components/ChatMessageComposer/ComposerTextInput/index';
import ConsoleDropArea from '@src/components/ChatMessageComposer/ConsoleDropArea';
import FormInputs from '@src/components/ChatMessageComposer/FormInputs';
import ImagePreviewDialogWrapper from '@src/components/ChatMessageComposer/ImagePreviewDialog';
import { ImproveTextAI } from '@src/components/ChatMessageComposer/ImproveTextAI';
import { MoreOptions } from '@src/components/ChatMessageComposer/MoreOptions';
import QuickMessages from '@src/components/ChatMessageComposer/QuickMessages';
import ChatViewerReportCAPIEvent from '@src/components/ChatViewer/ChatViewerReportCAPIEvent';
import ImageViewerContainer from '@src/components/ImageViewer';
import useConsoleConverTags from '@src/hooks/useConsoleConverTags';
import { ChatStatus } from '@src/model/frontendmodel';
import { setShowQuickMsg } from '@src/reducers/activeChatQuickMessages';
import { setAudioRecording } from '@src/reducers/audioRecording';
import { useGetConversionConfigQuery } from '@src/reducers/capiAPI';
import MetaIcon from '@src/resources/icons/MetaIcon';
import {
  isTransferInProgress,
  selectIsOutgoingCallDisabled,
} from '@src/selectors/agentConsole';
import {
  activeChatSelector,
  activeWrittenMessageSelector,
} from '@src/selectors/chats';
import StorageUtils from '@src/utils/browserstorage';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DARK_BG_CARD } from '@src/muithemes/darkModeColors';
import LinkPreviewWrapper from '../LinkPreviewWrapper';
import { LocalOfferOutlinedIcon } from '../MoreOptions/SvgIcons';
import { ResizeButtonsWrapper } from '../Utils';
import ChatBotDisabledMessage from './ChatBotDisabledMessage';
import ComposerAndSendButtonWrapper from './ComposerAndSendButtonWrapper';
import ServiceWindowExpiredMessage from './ServiceWindowExpiredMessage';
import StartOutgoingCallButton from './StartOutgoingCallButton';

const ComposerToolbar: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const composerBg = isDark ? DARK_BG_CARD : '#f4f4f5';

  const textInputRef = useRef<HTMLDivElement | null>(null);
  const uploadFilesFormRef = useRef<HTMLFormElement | null>(null);
  const imageFileInputRef = useRef<HTMLInputElement | null>(null);
  const documentFileInputRef = useRef<HTMLInputElement | null>(null);

  const [commandQuickMessage, setCommandQuickMessage] = React.useState('');
  const [maxRows, setMaxRows] = React.useState(1);

  const {
    root,
    textInput,
    chatQuotedMessagePreview,
    tags,
    tagsActive,
    tagsDisabled,
    tagsContainer,
    capiIcon,
    capiContainer,
  } = useStyles();

  const isCallDisabled = useSelector(selectIsOutgoingCallDisabled);
  const activeChat = useSelector(activeChatSelector);
  const activeWrittenMessage = useSelector(activeWrittenMessageSelector);
  const transferInProgress = useSelector(isTransferInProgress);

  const tagsButtonDissabled =
    transferInProgress ||
    !activeChat?.id ||
    activeChat.status === ChatStatus.BOT_CHATTING;

  const { toggleConsoleConverTagsVisible, consoleConverTagsVisible } =
    useConsoleConverTags();

  const [reportCapiVisible, setReportCapiVisible] = useState(false);

  const getQuickMessages = _.debounce(
    text => {
      setCommandQuickMessage(text);

      if (text?.[0] === '/') dispatch(setShowQuickMsg(true));
    },
    1000,
    { trailing: true }
  );

  const searchQuickMessages = () => {
    getQuickMessages('/');
  };

  useEffect(() => {
    setMaxRows(
      Number(StorageUtils.getFromLocalStorage('agentChatComposeTextRows')) || 1
    );
  }, []);

  const capiConfig = useGetConversionConfigQuery();
  const capiEnabled = capiConfig.data?.manual?.modules?.includes('CONSOLE');

  return (
    <>
      {consoleConverTagsVisible && (
        <Box className={tagsContainer} pt="10px" pl={2}>
          <ConverTagsListView
            withoutContextMenu
            currentCAPIEvents={activeChat?.conversionTypes || []}
          />
          {CustomDivider}
        </Box>
      )}
      <ChatBotDisabledMessage />
      <ServiceWindowExpiredMessage />
      {activeChat?.originatedFromAd && reportCapiVisible && (
        <Box className={capiContainer} pl={2}>
          <ChatViewerReportCAPIEvent
            module="CONSOLE"
            chatId={activeChat?.id}
            currentCAPIEvents={activeChat?.conversionTypes}
          />
          {CustomDivider}
        </Box>
      )}
      <Box
        display="flex"
        justifyContent="center"
        style={{ backgroundColor: composerBg }}
      >
        <ResizeButtonsWrapper
          handleShrinkComposeTextBox={() => {
            const val = Math.max(maxRows - 1, 1);
            setMaxRows(val);
            StorageUtils.setInLocalStorage(
              'agentChatComposeTextRows',
              String(val)
            );
          }}
          handleStretchComposeTextBox={() => {
            const val = Math.min(maxRows + 1, MAX_ROWS);
            setMaxRows(val);
            StorageUtils.setInLocalStorage(
              'agentChatComposeTextRows',
              String(val)
            );
          }}
        />
      </Box>
      <Box
        id="console-composer-toolbar-id"
        className={chatQuotedMessagePreview}
      >
        <Box style={{ position: 'relative' }}>
          <Box
            style={{
              position: 'absolute',
              bottom: 0,
              zIndex: 1000,
              width: '100%',
              backgroundColor: theme.palette.background.paper,
              boxShadow: 'rgba(0, 0, 0, 0.08) -4px 1px 8px 0px',
            }}
          >
            <ChatQuotedMessagePreview />
          </Box>
        </Box>
        <Box className={root}>
          <MoreOptions
            searchQuickMessages={searchQuickMessages}
            imageFileInputRef={imageFileInputRef}
            documentFileInputRef={documentFileInputRef}
            startAudioRecording={() => dispatch(setAudioRecording(true))}
          />
          <IconButton
            className={`${tags} ${consoleConverTagsVisible ? tagsActive : ''} ${
              tagsButtonDissabled ? tagsDisabled : ''
            }`}
            onClick={() => {
              toggleConsoleConverTagsVisible();
              setReportCapiVisible(false);
            }}
            disabled={tagsButtonDissabled}
          >
            <SvgIcon
              fontSize="inherit"
              style={{ width: '17px', height: '17px' }}
            >
              {LocalOfferOutlinedIcon}
            </SvgIcon>
          </IconButton>
          {capiEnabled && activeChat?.originatedFromAd && (
            <IconButton
              className={`${capiIcon} ${reportCapiVisible ? 'active' : ''}`}
              onClick={() => {
                setReportCapiVisible(!reportCapiVisible);
                if (consoleConverTagsVisible) toggleConsoleConverTagsVisible();
              }}
            >
              <MetaIcon />
            </IconButton>
          )}
          <ImproveTextAI disabled={!activeWrittenMessage?.length} />
          <LinkPreviewWrapper />
          <Box className={textInput}>
            <ComposerAndSendButtonWrapper
              textInputRef={textInputRef}
              getQuickMessages={getQuickMessages}
              maxRows={maxRows}
            />
            {!isCallDisabled && <StartOutgoingCallButton />}
          </Box>
          <QuickMessages
            textInputRef={textInputRef}
            command={commandQuickMessage}
            setCommandQuickMessage={setCommandQuickMessage}
          />
        </Box>
        <AttachFilesSummary />
        <ImageViewerContainer />
        <ConsoleDropArea />
        <ImagePreviewDialogWrapper />
        <FormInputs
          uploadFilesFormRef={uploadFilesFormRef}
          imageFileInputRef={imageFileInputRef}
          documentFileInputRef={documentFileInputRef}
        />
      </Box>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    padding: '14px',
    backgroundColor:
      theme.palette.type === 'dark' ? DARK_BG_CARD : theme.palette.grey[100],
    borderTop:
      theme.palette.type === 'dark'
        ? `1px solid ${theme.palette.divider}`
        : 'none',
    width: '100%',
    minHeight: '72px',
    borderRadius: '0px 0px 8px 8px',
    position: 'relative',
  },
  textInput: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: '8px',
  },
  chatQuotedMessagePreview: {
    backgroundColor:
      theme.palette.type === 'dark' ? DARK_BG_CARD : theme.palette.grey[100],
    borderRadius: '0px 0px 8px 8px',
  },
  tags: {
    width: '24px',
    height: '24px',
    padding: 0,
    borderRadius: '26px',
    border: `2px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    '&:hover': {
      border: `2px solid ${theme.palette.primary.main}`,
      color: '#FFFFFF',
      backgroundColor: theme.palette.primary.main,
    },
  },
  tagsActive: {
    border: `2px solid ${theme.palette.primary.main}`,
    color: '#FFFFFF',
    backgroundColor: theme.palette.primary.main,
  },
  tagsDisabled: {
    border: `2px solid ${theme.palette.grey[300]}`,
    color: '#FFFFFF',
  },
  tagsContainer: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
  },
  capiIcon: {
    width: '24px',
    height: '24px',
    padding: 0,
    borderRadius: '26px',
    border: `2px solid #478ECC`,
    color: '#478ECC',
    '&:hover, &.active': {
      border: `2px solid #478ECC`,
      color: '#FFFFFF',
      backgroundColor: '#478ECC',
    },
  },
  capiContainer: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
    padding: '12px 0 0 16px',
  },
}));

const CustomDivider = (
  <Divider
    style={{
      marginTop: '12px',
      marginRight: '16px',
      padding: 0,
    }}
  />
);

export default ComposerToolbar;
